import { getI18n } from '@/plugins/0.i18n'
import { formatPhone, formatSalonerName } from '@/utils/strings'

const i18n = getI18n().global

// Backend returns names with HTML entities (e.g. It&#39;s) — they would show up raw in the template
const decode = name => (name ? formatSalonerName(name) : name)

// Vuetify theme token per visit status id (ids as in statusOptions on the statistics/visits page)
const STATUS_COLORS = {
  1: 'green', // Zakończona
  2: 'blue', // Umówiona
  3: 'error', // Odwołana
  4: 'warning', // Niepotwierdzona
  5: 'grey', // Skasowana
  6: 'grey', // Nie pokazuj
  7: 'info', // Potwierdzona
  8: 'primary', // Klient w salonie
}

// Statuses that bring no money in — card gets dimmed
const MUTED_STATUS_IDS = [3, 5, 6]

/**
 * Icon per visit source, keyed by visitSource.id (salonerEventsDB::SALONER_REGISTER_*).
 * The label itself stays exactly as the backend sent it — only the icon is derived here.
 */
const SOURCE_ICONS = {
  1: 'ri-computer-line', // Panel saloner
  2: 'ri-global-line', // Portal / wizytówka
  3: 'ri-smartphone-line', // Aplikacja kliencka
  4: 'ri-store-line', // Aplikacja saloner
  5: 'ri-android-line', // Aplikacja kliencka (Android)
  6: 'ri-apple-line', // Aplikacja kliencka (iOS)
  7: 'ri-robot-line', // VoiceBot
}

const DEFAULT_SOURCE_ICON = 'ri-global-line'

// A standalone product sale has no event status, so it gets its own colour
const SALE_STATUS_COLOR = 'blue'

/**
 * Maps a single visit into the VisitCard shape.
 *
 * Shared by /statistics/visits/index and /clients/getVisitsAndSales — both endpoints return the
 * same VisitDto shape. The client history additionally sends kind, loyalty points, attachments
 * and the cancellation reason; those fields are simply absent on the statistics page.
 *
 * @param {Object} visit - raw visit from either endpoint
 * @returns {Object} VisitCardData
 */
export const mapStatsVisitToCard = visit => ({
  id: visit.id,
  kind: visit.kind || 'visit',
  date: visit.date,
  dateEnd: visit.dateEnd,
  duration: visit.duration,
  // Minutes from the visit's start to the moment it was finalised - the backend only fills it
  // in for finalised visits, so it stays null for everything else (see VisitDto::__construct).
  realDuration: visit.realDuration,
  status: {
    id: visit.kind === 'sale'
      ? 'store-selling'
      : visit.status?.id,
    name: visit.status?.name,
    color: visit.kind === 'sale'
      ? SALE_STATUS_COLOR
      : STATUS_COLORS[visit.status?.id] || 'grey',
  },
  description: visit.description,
  muted: MUTED_STATUS_IDS.includes(visit.status?.id),
  source: visit.visitSource?.name,
  sourceIcon: SOURCE_ICONS[visit.visitSource?.id] || DEFAULT_SOURCE_ICON,
  cancelReason: visit.cancelReason || null,
  cancelComment: visit.cancelComment || null,
  attachments: visit.attachments || [],
  loyaltyPoints: {
    gained: Number(visit.gainedLoyaltyPoints) || 0,
    spent: Number(visit.spentLoyaltyPoints) || 0,
  },
  employee: {
    // id: visit.employeeId,
    id: visit.employeeInfo?.id,
    name: decode(visit.employeeInfo?.name.trim()),
    shortName: decode(visit.employeeInfo?.shortName?.trim()),
    imageSource: visit.employeeInfo?.imageSource,
    color: visit.employeeInfo?.color,
    deleted: visit.employeeInfo?.deleted,
  },
  client: visit.salonerClient
    ? {
      id: visit.salonerClient.salonerClientId,
      name: decode(visit.salonerClient.name),
      phone: visit.salonerClient.phone ? formatPhone(visit.salonerClient.phone) : '',
      email: visit.salonerClient.email,
    }
    : null,
  services: (visit.services || []).map(service => ({
    title: decode(service.title),
    price: service.price,
    priceAfterDiscount: service.valueAfterDiscount,
    employee: service.employee,
  })),
  products: (visit.soldProducts || []).map(product => ({
    name: decode(product.productName),
    quantity: product.quantity,
    value: product.value,
    employee: product.employee,
    package: product.package,
    unit: product.unit,
  })),
  usedProducts: (visit.usedProducts || []).map(product => ({
    name: decode(product.productName),
    quantity: product.quantity,
    value: product.value,
    employee: product.employee,
    package: product.package,
    unit: product.unit,
  })),
  payments: visit.payForms || [],
  paid: visit.paid,
  tip: Number(visit.tip) || 0,
  discounts: [
    { label: i18n.t('services_plural'), value: Number(visit.servicesDiscount) },
    { label: i18n.t('sale'), value: Number(visit.productsDiscount) },
    { label: i18n.t('loyaltyProgram'), value: Number(visit.loyaltyProgramDiscount) },
  ].filter(discount => discount.value > 0),
})
